import { z } from "zod";
import { CURRENCIES, QR_REFERENCE_TYPES } from "../status.js";
import { SUPPORTED_LOCALES } from "../locales.js";

export const INDUSTRIES = [
  "landscaping",
  "general_services",
  "construction",
  "cleaning",
  "other",
] as const;

const emailTemplateSchema = z.object({
  subject: z.string().trim().min(1).max(300),
  body: z.string().trim().min(1).max(10000),
});

export const localizedEmailTemplatesSchema = z.object({
  en: emailTemplateSchema,
  de: emailTemplateSchema,
  fr: emailTemplateSchema,
  es: emailTemplateSchema,
});

export type LocalizedEmailTemplates = z.infer<
  typeof localizedEmailTemplatesSchema
>;

export const DEFAULT_OFFER_EMAIL_TEMPLATES: LocalizedEmailTemplates = {
  en: {
    subject: "{{companyName}} – Offer {{number}}",
    body: "Hello {{customerName}},\n\nPlease find our offer {{number}} attached.\n\nKind regards,\n{{companyName}}",
  },
  de: {
    subject: "{{companyName}} – Offerte {{number}}",
    body: "Guten Tag {{customerName}},\n\nim Anhang finden Sie unsere Offerte {{number}}.\n\nFreundliche Grüsse\n{{companyName}}",
  },
  fr: {
    subject: "{{companyName}} – Offre {{number}}",
    body: "Bonjour {{customerName}},\n\nVeuillez trouver notre offre {{number}} en pièce jointe.\n\nMeilleures salutations,\n{{companyName}}",
  },
  es: {
    subject: "{{companyName}} – Oferta {{number}}",
    body: "Hola {{customerName}},\n\nAdjuntamos nuestra oferta {{number}}.\n\nUn cordial saludo,\n{{companyName}}",
  },
};

export const DEFAULT_INVOICE_EMAIL_TEMPLATES: LocalizedEmailTemplates = {
  en: {
    subject: "{{companyName}} – Invoice {{number}}",
    body: "Hello {{customerName}},\n\nPlease find invoice {{number}} attached.\n\nKind regards,\n{{companyName}}",
  },
  de: {
    subject: "{{companyName}} – Rechnung {{number}}",
    body: "Guten Tag {{customerName}},\n\nim Anhang finden Sie die Rechnung {{number}}.\n\nFreundliche Grüsse\n{{companyName}}",
  },
  fr: {
    subject: "{{companyName}} – Facture {{number}}",
    body: "Bonjour {{customerName}},\n\nVeuillez trouver la facture {{number}} en pièce jointe.\n\nMeilleures salutations,\n{{companyName}}",
  },
  es: {
    subject: "{{companyName}} – Factura {{number}}",
    body: "Hola {{customerName}},\n\nAdjuntamos la factura {{number}}.\n\nUn cordial saludo,\n{{companyName}}",
  },
};

const optionalText = (max: number) =>
  z.string().trim().max(max).nullable().optional();

export const companySettingsSchema = z.object({
  name: z.string().trim().min(1).max(160).optional(),
  tradingName: optionalText(160),
  addressLine1: z.string().trim().min(1).max(120).optional(),
  addressLine2: optionalText(120),
  postalCode: z.string().trim().min(1).max(12).optional(),
  city: z.string().trim().min(1).max(80).optional(),
  canton: optionalText(40),
  country: z.string().trim().length(2).optional(),
  email: z.string().trim().toLowerCase().email().optional(),
  phone: optionalText(40),
  website: z.string().trim().url().nullable().optional(),
  vatNumber: optionalText(40),
  industry: z.enum(INDUSTRIES).optional(),
  commercialRegister: optionalText(120),
  useCompanyBranding: z.boolean().optional(),
});
export type CompanySettingsDto = z.infer<typeof companySettingsSchema>;

export const workSettingsSchema = z.object({
  defaultWorkdayMinutes: z.number().int().min(1).max(1_440).optional(),
  vacationWeeks: z
    .union([z.string(), z.number()])
    .pipe(z.coerce.number().min(0).max(20))
    .optional(),
});
export type WorkSettingsDto = z.infer<typeof workSettingsSchema>;

export const billingSettingsSchema = z.object({
  currency: z.enum(CURRENCIES).optional(),
  vatRegistered: z.boolean().optional(),
  paymentTermDays: z.number().int().min(0).max(365).optional(),
  bankName: optionalText(160),
  iban: optionalText(40),
  referenceType: z.enum(QR_REFERENCE_TYPES).optional(),
  /** QR-bill "additional information" (max. 140 characters per SIX). */
  defaultInvoiceMessage: optionalText(140),
  roundingStep: z.enum(["0.01", "0.05"]).optional(),
});
export type BillingSettingsDto = z.infer<typeof billingSettingsSchema>;

export const documentSettingsSchema = z.object({
  accentColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "Use a hex colour like #0f766e")
    .optional(),
  offerValidityDays: z.number().int().min(1).max(365).optional(),
  offerIntroText: optionalText(4000),
  offerTermsText: optionalText(4000),
  invoiceIntroText: optionalText(4000),
  invoiceTermsText: optionalText(4000),
  footerText: optionalText(500),
  offerEmailTemplates: localizedEmailTemplatesSchema.optional(),
  invoiceEmailTemplates: localizedEmailTemplatesSchema.optional(),
});
export type DocumentSettingsDto = z.infer<typeof documentSettingsSchema>;

export const localizationSettingsSchema = z.object({
  defaultLocale: z.enum(SUPPORTED_LOCALES).optional(),
  timezone: z.string().trim().min(1).max(80).optional(),
});
export type LocalizationSettingsDto = z.infer<
  typeof localizationSettingsSchema
>;

export const taxRateSchema = z.object({
  name: z.string().trim().min(1).max(80),
  rate: z
    .union([z.number().finite(), z.string().regex(/^\d{1,3}(\.\d{1,3})?$/)])
    .transform(String),
  isDefault: z.boolean().default(false),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).optional(),
});

const unitTranslationSchema = z.object({
  name: z.string().trim().min(1).max(80),
});

export const unitTranslationsSchema = z
  .object({
    de: unitTranslationSchema.optional(),
    fr: unitTranslationSchema.optional(),
    es: unitTranslationSchema.optional(),
  })
  .optional();

export const unitSchema = z.object({
  code: z.string().trim().min(1).max(20),
  name: z.string().trim().min(1).max(80),
  active: z.boolean().default(true),
  sortOrder: z.number().int().min(0).optional(),
  translations: unitTranslationsSchema,
});
export type UnitDto = z.infer<typeof unitSchema>;
export const unitUpdateSchema = unitSchema.omit({ code: true }).partial();
export type UnitUpdateDto = z.infer<typeof unitUpdateSchema>;

export interface UnitResponse {
  id: string;
  code: string;
  name: string;
  active: boolean;
  sortOrder: number;
  translations: { de?: string; fr?: string; es?: string };
}

export const numberSequenceSchema = z.object({
  prefix: z.string().trim().max(12),
  includeYear: z.boolean(),
  padding: z.number().int().min(3).max(10),
});

/** GET /settings/commercial — rules document editors need. */
export interface CommercialSettingsResponse {
  currency: string;
  vatRegistered: boolean;
  roundingStep: "0.01" | "0.05";
  paymentTermDays: number;
  ibanConfigured: boolean;
  offerValidityDays: number;
  taxRates: Array<{
    id: string;
    name: string;
    rate: string;
    isDefault: boolean;
  }>;
  defaultTaxRate: string;
}
